/**
 * Minimal cookie helpers. Domain is supplied by callers (root_domain from config).
 *
 * Cookies are set with `Domain=.{domain}; Path=/; SameSite=Lax; Secure;`.
 * `Secure` is fine because the SDK only runs over HTTPS in practice; if a host
 * embeds it on http://localhost the cookie just won't stick (acceptable).
 */

export interface CookieOptions {
  /** Days until expiry; omit for a session cookie. */
  days?: number;
  /** Cookie domain; will be prefixed with a leading dot. Omit for host-only cookies. */
  domain?: string;
  /** Defaults to '/'. */
  path?: string;
  /** Defaults to 'Lax'. */
  sameSite?: 'Strict' | 'Lax' | 'None';
}

const DAY_MS = 24 * 60 * 60 * 1000;

export function getCookie(name: string): string | null {
  if (typeof document === 'undefined') return null;
  const target = `${name}=`;
  for (const part of document.cookie.split(';')) {
    const trimmed = part.trim();
    if (trimmed.startsWith(target)) {
      return decodeURIComponent(trimmed.slice(target.length));
    }
  }
  return null;
}

export function setCookie(name: string, value: string, opts: CookieOptions = {}): void {
  if (typeof document === 'undefined') return;
  const segments = [`${name}=${encodeURIComponent(value)}`];

  if (opts.days !== undefined) {
    const expires = new Date(Date.now() + opts.days * DAY_MS);
    segments.push(`Expires=${expires.toUTCString()}`);
  }
  segments.push(`Path=${opts.path ?? '/'}`);
  if (opts.domain) {
    const dotted = opts.domain.startsWith('.') ? opts.domain : `.${opts.domain}`;
    segments.push(`Domain=${dotted}`);
  }
  segments.push(`SameSite=${opts.sameSite ?? 'Lax'}`);
  segments.push('Secure');

  document.cookie = segments.join('; ');
}

export function deleteCookie(name: string, opts: Pick<CookieOptions, 'domain' | 'path'> = {}): void {
  if (typeof document === 'undefined') return;
  const segments = [`${name}=`, 'Expires=Thu, 01 Jan 1970 00:00:00 GMT', `Path=${opts.path ?? '/'}`];
  if (opts.domain) {
    const dotted = opts.domain.startsWith('.') ? opts.domain : `.${opts.domain}`;
    segments.push(`Domain=${dotted}`);
  }
  segments.push('SameSite=Lax');
  segments.push('Secure');
  document.cookie = segments.join('; ');
}
